import {
  type DictatedReportSave,
  type DictationReport,
  type DictationReportInput,
  dictationReportSchema,
  encodeDictationTemplate,
  parseDictationTemplate,
  privateTemplateListSchema,
} from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { referenceClient } from "@/lib/orpc/client";
import { queryClient } from "@/lib/query-client";
import {
  getVisitScope,
  refreshVisitReads,
  useVisitScope,
} from "@/lib/visit-queries";

// Dictation templates (radiology dictation mode): private templates whose
// instructions hold a report with [ ] fields and the spoken aliases that
// pick it by voice (see dictation-report.ts in @rosc/contracts). They are
// listed apart from note templates (GET /templates?kind=dictation).

export type DictationTemplate = {
  id: string;
  name: string;
  aliases: string[];
  body: string;
  // The radiologist's own rules for this template ("" for none).
  rules: string;
};

const listKey = (uid: string | undefined) => ["dictation-templates", uid ?? ""];

const context = () => ({ expectedUserUid: getVisitScope()?.uid });

export const fetchDictationTemplates = async (): Promise<
  DictationTemplate[]
> => {
  const items: DictationTemplate[] = [];
  let cursor: string | undefined;
  do {
    const page = privateTemplateListSchema.parse(
      await referenceClient.listTemplates(
        {
          query: {
            limit: "100",
            kind: "dictation",
            ...(cursor ? { cursor } : {}),
          },
        },
        { context: context() },
      ),
    );
    for (const item of page.items) {
      const content = parseDictationTemplate(item.optionalText);
      if (content)
        items.push({ id: item.public_id, name: item.name, ...content });
    }
    cursor = page.nextCursor ?? undefined;
  } while (cursor);
  return items;
};

export function useDictationTemplates(enabled = true): {
  templates: DictationTemplate[];
  loading: boolean;
  error: boolean;
} {
  const scope = useVisitScope();
  const query = useQuery(
    {
      queryKey: listKey(scope?.uid),
      queryFn: fetchDictationTemplates,
      enabled: enabled && !!scope,
      staleTime: 30_000,
      retry: false,
    },
    queryClient,
  );
  return {
    templates: query.data ?? [],
    loading: enabled && !!scope && query.isPending,
    error: query.isError,
  };
}

const refresh = () =>
  queryClient.invalidateQueries({ queryKey: listKey(getVisitScope()?.uid) });

export const saveDictationTemplate = async (
  template: Omit<DictationTemplate, "id"> & { id?: string },
): Promise<void> => {
  const optionalText = encodeDictationTemplate(template);
  const name = template.name.trim();
  if (template.id)
    await referenceClient.updateTemplate(
      {
        params: { public_id: template.id },
        query: {},
        body: { name, headers: [], optionalText },
      },
      { context: context() },
    );
  else
    await referenceClient.createTemplate(
      {
        query: {},
        body: { name, headers: [], optionalText, isDefault: false },
      },
      { context: context() },
    );
  await refresh();
};

export const deleteDictationTemplate = async (id: string): Promise<void> => {
  await referenceClient.deleteTemplate(
    { params: { public_id: id }, query: {} },
    { context: context() },
  );
  await refresh();
};

export const fillDictationReport = async (
  body: DictationReportInput,
  signal?: AbortSignal,
): Promise<DictationReport> =>
  dictationReportSchema.parse(
    await referenceClient.createDictationReport(
      { query: {}, body },
      { context: context(), signal },
    ),
  );

// Saved reports (visits with status "Dictated report"): created on the
// first save, then replaced on every later one. Returns the visit id.
export const saveDictatedReport = async (
  body: DictatedReportSave,
  id: string | null,
): Promise<string> => {
  const saved = id
    ? await referenceClient.updateDictatedReport(
        { params: { public_id: id }, query: {}, body },
        { context: context() },
      )
    : await referenceClient.createDictatedReport(
        { query: {}, body },
        { context: context() },
      );
  void refreshVisitReads();
  return saved.public_id;
};

export const deleteDictatedReport = async (id: string): Promise<void> => {
  await referenceClient.deleteVisit(
    { params: { public_id: id }, query: {} },
    { context: context() },
  );
  await refreshVisitReads();
};
